import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
const apiUrl = import.meta.env.VITE_API_URL;
// Define the shape of our user object
interface User {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  videos_left: number;
  created_at: string;
  plan_id?: string;
  stripe_customer_id?: string;
  customer_subscription_id?: string;
  plan_started_at?: string;
  is_premium: string;
}

// JWT token interface
interface DecodedToken {
  exp: number;
  sub: string;
}

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  logout: () => void;
  refreshUserData: () => Promise<void>;
  useVideoCredits: (creditsUsed: number) => Promise<boolean>;
  isPremiumUser: () => boolean;
  getPlanName: () => string | null;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  token: null,
  logout: () => {},
  refreshUserData: async () => {},
  useVideoCredits: async () => false,
  isPremiumUser: () => false,
  getPlanName: () => null,
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshingToken, setRefreshingToken] = useState(false);

  // Function to check if token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const decodedToken = jwtDecode<DecodedToken>(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch (error) {
      console.error("Error decoding token:", error);
      return true;
    }
  };

  // Function to fetch user data from the backend
  const fetchUserData = async (authToken: string) => {
    try {
      const response = await axios.get(`${apiUrl}/me`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Token might be expired, try to refresh it
        const newToken = await refreshToken(authToken);
        if (newToken) {
          return fetchUserData(newToken);
        }
      }
      return null;
    }
  };

  // Function to refresh the token
  const refreshToken = async (oldToken: string): Promise<string | null> => {
    if (refreshingToken) {
      return null;
    }

    try {
      setRefreshingToken(true);
      const response = await axios.post(`${apiUrl}/api/auth/refresh-token`, {
        token: oldToken,
      });

      const newToken = response.data.access_token;
      localStorage.setItem("auth_token", newToken);
      setToken(newToken);
      setRefreshingToken(false);
      return newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      setRefreshingToken(false);
      logout();
      return null;
    }
  };

  // Function to refresh user data
  const refreshUserData = async () => {
    if (token) {
      await fetchUserData(token);
    }
  };

  // Function to handle logout
  const logout = () => {
    localStorage.removeItem("auth_token");
    setUser(null);
    setToken(null);
  };

  // Function to use video credits
  const useVideoCredits = async (creditsUsed: number): Promise<boolean> => {
    if (!token || !user) return false;

    // Check and refresh token if needed
    if (token && isTokenExpired(token)) {
      const newToken = await refreshToken(token);
      if (!newToken) return false;
    }

    try {
      const response = await axios.post(
        `${apiUrl}/api/use-video-credits`,
        { credits_used: creditsUsed },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update user with new credits count
      setUser({
        ...user,
        videos_left: response.data.videos_left,
      });

      return true;
    } catch (error) {
      console.error("Error using video credits:", error);

      // Check if it's an axios error with response
      if (axios.isAxiosError(error) && error.response) {
        // Handle specific errors
        if (error.response.status === 400) {
          // Not enough credits error
          alert(
            error.response.data.detail || "Not enough video credits available."
          );
        } else if (error.response.status === 401) {
          // Authentication error
          alert("You need to log in again.");
          logout();
        } else {
          // Generic error
          alert("An error occurred while processing your request.");
        }
      } else {
        // Network error or unexpected error
        alert(
          "There was a problem connecting to the server. Please try again."
        );
      }

      return false;
    }
  };

  // Setup token refresh interval
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;

    if (token) {
      // Check token every minute and refresh if needed
      refreshInterval = setInterval(async () => {
        if (isTokenExpired(token)) {
          await refreshToken(token);
        }
      }, 60000); // Check every minute
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [token]);

  // Check for token and fetch user data on initial load
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      const storedToken = localStorage.getItem("auth_token");

      if (storedToken) {
        // If token is expired, try to refresh it
        if (isTokenExpired(storedToken)) {
          const newToken = await refreshToken(storedToken);
          if (newToken) {
            await fetchUserData(newToken);
          }
        } else {
          setToken(storedToken);
          await fetchUserData(storedToken);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // The value that will be provided to consumers of this context
  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    token,
    logout,
    refreshUserData,
    useVideoCredits,
    isPremiumUser: () => !!user?.plan_id,
    getPlanName: () => user?.plan_id || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
